import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldAlert, CheckCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/** One-click sign-in landing page reached from the welcome / abandon emails. */
export default function MagicLogin() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const next = params.get("next") || "/dashboard";
  const { magicLogin } = useAuth();
  const [state, setState] = useState("redeeming"); // redeeming | ok | error
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const ran = useRef(false); // React 18 dev-mode double-invoke guard

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); setError("Missing login token."); return; }
    (async () => {
      try {
        await magicLogin(token);
        setState("ok");
        setTimeout(() => navigate(next, { replace: true }), 800);
      } catch (e) {
        setState("error");
        setError(e?.response?.data?.detail || "This login link is no longer valid.");
      }
    })();
  }, [token, next, magicLogin, navigate]);

  return (
    <div data-testid="magic-login-page" className="max-w-md mx-auto px-6 py-24 text-center">
      {state === "redeeming" && (
        <>
          <Loader2 className="w-10 h-10 text-[#8C4A57] animate-spin mx-auto mb-5" />
          <h1 className="font-serif text-2xl text-[#2D2628] mb-2">Signing you in…</h1>
          <p className="text-[#6B5E62] text-sm">This will just take a moment.</p>
        </>
      )}
      {state === "ok" && (
        <>
          <CheckCircle className="w-12 h-12 text-[#8C4A57] mx-auto mb-5" />
          <h1 className="font-serif text-2xl text-[#2D2628] mb-2">You're signed in.</h1>
          <p className="text-[#6B5E62] text-sm">Redirecting to your dashboard…</p>
        </>
      )}
      {state === "error" && (
        <>
          <ShieldAlert className="w-12 h-12 text-[#8C4A57] mx-auto mb-5" />
          <h1 className="font-serif text-2xl text-[#2D2628] mb-2">Link no longer valid</h1>
          <p className="text-[#6B5E62] text-sm mb-6" data-testid="magic-login-error">{error}</p>
          <Link to="/login" className="btn-primary" data-testid="magic-login-fallback">
            Sign in with your email
          </Link>
        </>
      )}
    </div>
  );
}
